import { MinimizedShift, MinimizedShiftStatus } from "@/components/shifts/MinimizedShift";

// Calendar Visualization - Minimalized realistic shift calendar
// Uses CSS group-hover from parent container
export const CalendarVisualization = () => {

  // Demo shift data - 2 days with multiple rows (realistic scheduling)
  // Each day has multiple rows to show overlapping/parallel shifts
  const demoShifts: Array<{
    day: string;
    rows: Array<Array<{
      id: string;
      title: string;
      start: number;
      end: number;
      employee: string;
      status: MinimizedShiftStatus;
    }>>;
  }> = [
    { day: 'MON', rows: [
      // Row 1: Morning done, afternoon in progress
      [
        { id: 'mon-1-1', title: 'Open', start: 8, end: 14, employee: 'JD', status: 'completed' },
        { id: 'mon-1-2', title: 'Close', start: 15, end: 20, employee: 'MK', status: 'in_progress' }
      ],
      // Row 2: Full day manager - currently active
      [
        { id: 'mon-2-1', title: 'Manager', start: 9, end: 18, employee: 'AS', status: 'in_progress' }
      ],
      // Row 3: Part-time help - completed
      [
        { id: 'mon-3-1', title: 'Support', start: 11, end: 15, employee: 'KL', status: 'completed' }
      ]
    ]},
    { day: 'TUE', rows: [
      // Row 1: All pending for tomorrow
      [
        { id: 'tue-1-1', title: 'Morning', start: 8, end: 13, employee: 'MK', status: 'pending' },
        { id: 'tue-1-2', title: 'Evening', start: 16, end: 20, employee: 'JD', status: 'pending' }
      ],
      // Row 2: Pending
      [
        { id: 'tue-2-1', title: 'Mid-Day', start: 10, end: 16, employee: 'AS', status: 'pending' }
      ],
      // Row 3: Planning stage
      [
        { id: 'tue-3-1', title: 'Training', start: 9, end: 12, employee: 'KL', status: 'planning' },
        { id: 'tue-3-2', title: 'Floor', start: 14, end: 19, employee: 'TP', status: 'planning' }
      ]
    ]}
  ];

  // Time range for the grid (8:00 - 20:00)
  const startHour = 8;
  const endHour = 20;
  const totalHours = endHour - startHour;

  // Opening hours (business is open 9:00 - 18:00)
  const openingHour = 9;
  const closingHour = 18;

  // Calculate shift position and width as percentages
  const getShiftStyle = (start: number, end: number) => {
    const left = ((start - startHour) / totalHours) * 100;
    const width = ((end - start) / totalHours) * 100;
    return { left: `${left}%`, width: `${width}%` };
  };

  // Calculate opening hours background position
  const openingHoursStyle = {
    left: `${((openingHour - startHour) / totalHours) * 100}%`,
    width: `${((closingHour - openingHour) / totalHours) * 100}%`
  };

  return (
    <div className="relative w-full h-full min-h-[280px] p-3 overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="grid grid-cols-12 h-full">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="border-r border-foreground last:border-r-0" />
          ))}
        </div>
      </div>

      <div className="relative z-10 h-full flex flex-col">
        {/* Time header */}
        <div className="flex mb-2">
          <div className="w-8 shrink-0" /> {/* Day label spacer */}
          <div className="flex-1 flex justify-between px-1">
            {[8, 10, 12, 14, 16, 18, 20].map((hour) => (
              <span
                key={hour}
                className="text-[9px] text-muted-foreground/60 font-medium tabular-nums"
              >
                {hour}:00
              </span>
            ))}
          </div>
        </div>

        {/* Day sections with multiple rows */}
        <div className="relative flex-1 flex flex-col gap-2">
          {demoShifts.map((dayData, dayIndex) => {
            // MON is the "current" day with the time indicator
            const isCurrentDay = dayData.day === 'MON';

            return (
              <div
                key={dayData.day}
                className="flex animate-in fade-in slide-in-from-left-2"
                style={{ animationDelay: `${dayIndex * 100}ms`, animationFillMode: 'both' }}
              >
                {/* Day label - spans all rows */}
                <div className="w-8 shrink-0 flex items-start pt-1">
                  <span className="text-[10px] font-semibold text-foreground/80">{dayData.day}</span>
                </div>

                {/* Rows container */}
                <div className="relative flex-1 flex flex-col gap-[2px]">
                  {dayData.rows.map((row, rowIndex) => (
                    <div
                      key={`${dayData.day}-row-${rowIndex}`}
                      className="relative h-[24px] bg-muted/10 rounded-sm border border-border/30"
                    >
                      {/* Opening hours background */}
                      <div
                        className="absolute inset-y-0 bg-primary/5 border-x border-primary/10"
                        style={openingHoursStyle}
                      />

                      {/* Hour dividers */}
                      <div className="absolute inset-0 flex">
                        {Array.from({ length: totalHours }).map((_, i) => (
                          <div
                            key={i}
                            className="flex-1 border-r border-border/20 last:border-r-0"
                          />
                        ))}
                      </div>

                      {/* Shifts in this row */}
                      {row.map((shift) => {
                        const style = getShiftStyle(shift.start, shift.end);
                        return (
                          <div
                            key={shift.id}
                            className="absolute top-[2px] bottom-[2px] z-10"
                            style={style}
                          >
                            <MinimizedShift
                              title={shift.title}
                              startHour={shift.start}
                              endHour={shift.end}
                              employee={shift.employee}
                              status={shift.status}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  {/* Current time indicator - only on MON (current day) */}
                  {isCurrentDay && (
                    <div
                      className="absolute top-0 bottom-0 w-[2px] bg-red-500/80 rounded-full z-20 shadow-sm"
                      style={{ left: `${((15 - startHour) / totalHours) * 100}%` }}
                    >
                      <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-red-500 shadow" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Fade to bottom - indicates calendar continues */}
          <div className="absolute -bottom-3 -left-3 -right-3 h-36 bg-gradient-to-t from-sidebar via-sidebar/90 to-transparent pointer-events-none z-30" />
        </div>

        {/* Legend */}
        <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-border/20">
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500/70" />
            <span className="text-[7px] text-muted-foreground/50">Done</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/70" />
            <span className="text-[7px] text-muted-foreground/50">Active</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500/70" />
            <span className="text-[7px] text-muted-foreground/50">Pending</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500/70" />
            <span className="text-[7px] text-muted-foreground/50">Plan</span>
          </div>
        </div>
      </div>
    </div>
  );
};
